// Libs
import React, { useState, useContext } from "react";
import { StoresContext, Stores } from "Globals/Stores";
import { useObserver } from "@shoothill/core";

import { Button, Table, TableHead, TableBody, TableRow, TableContainer, TableCell, Box, Checkbox, Typography } from "@material-ui/core";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";
import { BinSVG } from "Globals/Views/IconSVGs/BinSVG";

// App
import { theme } from "Globals/Styles/AppTheme";
import IncomeDetailsViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsViewModel";
import OutstandingLoanViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/OutstandingLoanViewModel";
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import { CurrencyInput } from "Globals/Views/CurrencyInput";

// Styles
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { Row } from "Globals/Styles/LayoutStyles";
import { CustomDatePicker } from "Globals/Views/CustomDatePicker";
import { CustomInput } from "Globals/Styles/AppStyling";

export const StyledAddButton = styled(StyledButton)`
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0px;
    margin: 0 0 1px 0;
`;
interface IProps {
    incomeDetailsViewModel: IncomeDetailsViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
    isPrimary?: boolean;
}

export const OutstandingLoanView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [upsertViewModel] = useState(() => new OutstandingLoanViewModel());

    return useObserver(() => (
        <>
            {(upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving) == false && (
                <>
                    <Row style={{ alignItems: "center" }}>
                        <CustomInput
                            label="Loan is in the name of"
                            placeHolder="Enter the name of who the loan holder"
                            viewModel={upsertViewModel}
                            fieldName="loanIsInNameOf"
                            shrink={true}
                            inputProps={{
                                disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                            }}
                        />
                        <CustomInput
                            type="select"
                            style={{ minWidth: "350px" }}
                            label="Purpose of loan / credit agreement"
                            placeHolder="Select the purpose of the loan"
                            viewModel={upsertViewModel}
                            fieldName="loanPurposeId"
                            selectItems={storesContext.Domain.LoanPurposeTypesStore.getLoanPurposeTypeOptions}
                            inputProps={{
                                disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                            }}
                        />
                        <CustomInput
                            type="select"
                            style={{ minWidth: "350px" }}
                            label="Name of lender"
                            viewModel={upsertViewModel}
                            fieldName="lenderId"
                            selectItems={storesContext.Domain.LenderTypeStore.getLenderTypeOptions}
                            inputProps={{
                                disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                            }}
                        />

                        {upsertViewModel.getModel.getIsLenderOther && (
                            <CustomInput
                                label="Other lender's name"
                                placeHolder="Enter the name of the lender"
                                viewModel={upsertViewModel}
                                fieldName="otherLender"
                                shrink={true}
                                inputProps={{
                                    disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                                }}
                            />
                        )}
                    </Row>
                    <Row style={{ alignItems: "center" }}>
                        <CurrencyInput
                            viewModel={upsertViewModel}
                            label={"Monthly payment"}
                            placeholder={"Enter monthly payment"}
                            fieldName={"monthlyPayment"}
                            disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                        />
                        <CurrencyInput
                            viewModel={upsertViewModel}
                            label={"Total amount outstanding"}
                            placeholder={"Enter total amount outstanding"}
                            fieldName={"totalAmountOutstanding"}
                            disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                        />
                        <Box>
                            <CustomDatePicker
                                viewModel={upsertViewModel}
                                label="Date of final payment"
                                placeholder={"Enter date of final payment"}
                                fieldName="dateOfFinalPayment"
                                value={upsertViewModel.model.dateOfFinalPayment}
                                onChange={(date) => {
                                    upsertViewModel.setDate("dateOfFinalPayment", date);
                                    props.incomeDetailsViewModel.restartTimer();
                                }}
                                resetCallback={props.incomeDetailsViewModel.restartTimer}
                                disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                            />
                        </Box>
                        {props.isPrimary && props.applicationFormViewModel.getSortedApplicantViewModels.length > 1 && (
                            <Box>
                                <Row style={{ alignItems: "center" }}>
                                    <Checkbox
                                        checked={upsertViewModel.getValue("sameForApplicantTwo")}
                                        onChange={(e) => {
                                            upsertViewModel.handleCheckboxChange("sameForApplicantTwo", e.target.checked);
                                        }}
                                        icon={<CheckboxSVG />}
                                        checkedIcon={<CheckboxCheckedSVG />}
                                        disabled={props.applicationFormViewModel.model.isSubmitted}
                                    />
                                    <Typography variant="body1" style={{ fontWeight: "bold" }}>
                                        Same for applicant 2
                                    </Typography>
                                </Row>
                            </Box>
                        )}

                        <Box style={{ display: "flex", alignItems: "start", marginTop: "22px" }}>
                            <StyledAddButton
                                variant="contained"
                                disableElevation
                                onClick={() => upsertViewModel.stash(props.incomeDetailsViewModel)}
                                disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                            >
                                +
                            </StyledAddButton>
                        </Box>
                    </Row>
                </>
            )}

            {props.incomeDetailsViewModel.getSortedOutstandingLoanViewModels.length > 0 && (
                <TableContainer>
                    <Table style={{ width: "100%" }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Loan is in the name of</TableCell>
                                <TableCell align="center">Purpose of loan / credit agreement</TableCell>
                                <TableCell align="center">Name of lender</TableCell>
                                <TableCell align="center">Monthly payment</TableCell>
                                <TableCell align="center">Amount outstanding</TableCell>
                                <TableCell align="center">Date of final payment due</TableCell>
                                {props.isPrimary && <TableCell align="center">Same for applicant 2</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.incomeDetailsViewModel.getSortedOutstandingLoanViewModels.map((loan) => (
                                <TableRow key={loan.model.id}>
                                    <TableCell component="th" scope="loan">
                                        {loan.model.loanIsInNameOf}
                                    </TableCell>
                                    <TableCell align="center">{storesContext.Domain.LoanPurposeTypesStore.getPurposeName(loan.model.loanPurposeId)}</TableCell>
                                    <TableCell align="center">{storesContext.Domain.LenderTypeStore.getLendersName(loan.model.lenderId)}</TableCell>
                                    <TableCell align="center">£{loan.model.monthlyPayment}</TableCell>
                                    <TableCell align="center">£{loan.model.totalAmountOutstanding}</TableCell>
                                    <TableCell align="center">{loan.getFormattedFinalPaymentDate}</TableCell>
                                    {props.isPrimary && <TableCell align="center">{loan.getValue("sameForApplicantTwo") ? "Yes" : "No"}</TableCell>}
                                    <TableCell align="center">
                                        {(upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving) == false && (
                                            <Button
                                                onClick={() => {
                                                    loan.delete(props.incomeDetailsViewModel);
                                                }}
                                            >
                                                <BinSVG />
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    ));
};
